import type { Mat4, PlaneName, Vec2, Vec3 } from "./types.ts";

export const DEG = Math.PI / 180;

/** Identity, column-major. */
export function identity(): Mat4 {
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}

/** Column-major matrix that maps a sketch drawn on `plane` (local u,v,normal) into world space. */
export function planeMatrix(plane: PlaneName): Mat4 {
  switch (plane) {
    case "XY":
      return identity();
    // local x -> Y, local y -> Z, normal -> +X  (CadQuery "YZ")
    case "YZ":
      return [0, 1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 0, 1];
    // local x -> X, local y -> Z, normal -> -Y  (CadQuery "XZ")
    case "XZ":
      return [1, 0, 0, 0, 0, 0, 1, 0, 0, -1, 0, 0, 0, 0, 0, 1];
  }
}

export function translation(v: Vec3): Mat4 {
  const m = identity();
  m[12] = v[0];
  m[13] = v[1];
  m[14] = v[2];
  return m;
}

/** Rodrigues rotation about a unit axis through the origin. */
export function axisRotation(axis: Vec3, degrees: number): Mat4 {
  const len = Math.hypot(axis[0], axis[1], axis[2]);
  if (len === 0) return identity();
  const [x, y, z] = [axis[0] / len, axis[1] / len, axis[2] / len];
  const a = degrees * DEG;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const t = 1 - c;
  // Row-major R, then written column-major below.
  const r00 = t * x * x + c;
  const r01 = t * x * y - s * z;
  const r02 = t * x * z + s * y;
  const r10 = t * x * y + s * z;
  const r11 = t * y * y + c;
  const r12 = t * y * z - s * x;
  const r20 = t * x * z - s * y;
  const r21 = t * y * z + s * x;
  const r22 = t * z * z + c;
  return [r00, r10, r20, 0, r01, r11, r21, 0, r02, r12, r22, 0, 0, 0, 0, 1];
}

/** Rotation about an axis through `point`. */
export function rotationAround(point: Vec3, axis: Vec3, degrees: number): Mat4 {
  const r = axisRotation(axis, degrees);
  // translation = p - R p
  const px = point[0];
  const py = point[1];
  const pz = point[2];
  const rx = r[0] * px + r[4] * py + r[8] * pz;
  const ry = r[1] * px + r[5] * py + r[9] * pz;
  const rz = r[2] * px + r[6] * py + r[10] * pz;
  r[12] = px - rx;
  r[13] = py - ry;
  r[14] = pz - rz;
  return r;
}

/** a * b (apply b first, then a). */
export function multiply(a: Mat4, b: Mat4): Mat4 {
  const out = new Array<number>(16).fill(0);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) sum += a[k * 4 + row] * b[col * 4 + k];
      out[col * 4 + row] = sum;
    }
  }
  return out;
}

export function applyMat4(m: Mat4, v: Vec3): Vec3 {
  return [
    m[0] * v[0] + m[4] * v[1] + m[8] * v[2] + m[12],
    m[1] * v[0] + m[5] * v[1] + m[9] * v[2] + m[13],
    m[2] * v[0] + m[6] * v[1] + m[10] * v[2] + m[14],
  ];
}

export function arcPoints(
  center: Vec2,
  radius: number,
  startDeg: number,
  endDeg: number,
  segments: number,
  clockwise = false,
): Vec2[] {
  let a0 = startDeg * DEG;
  let a1 = endDeg * DEG;
  if (clockwise) {
    while (a1 > a0) a1 -= 2 * Math.PI;
  } else {
    while (a1 < a0) a1 += 2 * Math.PI;
  }
  const n = Math.max(1, Math.round(segments));
  const pts: Vec2[] = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    pts.push([center[0] + radius * Math.cos(a), center[1] + radius * Math.sin(a)]);
  }
  return pts;
}

/** Deterministic JSON with sorted keys, for cache keys and hashing. */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
}

/** cyrb53: fast 53-bit string hash, good enough for in-memory/IndexedDB cache keys. */
export function hash53(str: string, seed = 0): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

export function buildCacheKey(designId: string, version: string, params: unknown, lod: string): string {
  return `${designId}@${version}:${lod}:${hash53(stableStringify(params))}`;
}
